// Shared `single`/`grid` widget-layout renderer — used by both the
// authenticated dashboard detail page and the anonymous public share view.
// Layout-only: the caller supplies how to render each widget's contents.
import type { ReactNode, CSSProperties } from 'react';

import { EmptyState } from '../common/EmptyState';

export interface ILayoutWidget {
  id: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const widgetChrome: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md, 6px)',
  overflow: 'hidden',
  background: 'var(--color-bg-surface, transparent)',
};

const widgetTitleBar: CSSProperties = {
  padding: '2px var(--sp-2)',
  borderBottom: '1px solid var(--color-border)',
  flex: 'none',
};

export function DashboardLayout<W extends ILayoutWidget>({
  kind,
  gridCols,
  gridRows,
  primaryWidgetId,
  widgets,
  renderWidget,
}: {
  kind: 'single' | 'grid';
  gridCols: number;
  gridRows: number;
  primaryWidgetId: string | null;
  widgets: W[];
  renderWidget: (widget: W) => ReactNode;
}) {
  const [firstWidget] = widgets;
  if (widgets.length === 0 || !firstWidget) {
    return (
      <EmptyState
        title="No widgets yet"
        description="This dashboard is AI-managed — its agent hasn't added widgets yet (dashboard_create_widget)."
      />
    );
  }

  if (kind === 'single') {
    const primary = widgets.find((w) => w.id === primaryWidgetId) ?? firstWidget;
    return <div style={{ ...widgetChrome, flex: 1, border: 0 }}>{renderWidget(primary)}</div>;
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        gridTemplateRows: `repeat(${gridRows}, 1fr)`,
        gap: 'var(--sp-2)',
        overflow: 'hidden',
      }}
    >
      {widgets.map((w) => (
        <div
          key={w.id}
          style={{
            ...widgetChrome,
            gridColumn: `${w.x + 1} / span ${w.w}`,
            gridRow: `${w.y + 1} / span ${w.h}`,
          }}
        >
          <div className="t-xs t-muted" style={widgetTitleBar}>
            {w.title}
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>{renderWidget(w)}</div>
        </div>
      ))}
    </div>
  );
}
